import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import LoadingSpinner from '../../components/LoadingSpinner';
import AmbulanceBookingForm from '../../components/bookings/AmbulanceBookingForm';
import { casesService } from '../../services/cases.service';
import { ambulanceBookingService } from '../../services/ambulance-booking.service';

interface AmbulanceBookingData {
  tripType: 'PRIORITY' | 'REGULAR';
  originAddress: string;
  destinationAddress: string;
  originCoordinates?: { lat: number; lng: number };
  destinationCoordinates?: { lat: number; lng: number };
  accessNotes: string;
  onSiteContact: {
    name: string;
    phone: string;
    shareWithProvider: boolean;
  };
  ambulanceType: string;
  specialRequests?: string;
}

interface PricingResult {
  basePrice: number;
  emergencySurge: number;
  total: number;
  breakdown: string;
  currency?: string;
}

const AmbulanceBookingPage: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [existingCases, setExistingCases] = useState<any[]>([]);
  const [selectedCase, setSelectedCase] = useState<string | null>(null);
  const [showBookingForm, setShowBookingForm] = useState(false);

  useEffect(() => {
    loadExistingCases();
  }, []);

  const loadExistingCases = async () => {
    try {
      const cases = await casesService.getCases();
      setExistingCases(cases || []);
    } catch (error) {
      console.error('Failed to load cases:', error);
      toast.error('Failed to load existing cases');
    }
  };

  const handleCaseSelect = (caseId: string) => {
    setSelectedCase(caseId);
    setShowBookingForm(true);
  };

  const handleNewBooking = () => {
    setSelectedCase(null);
    setShowBookingForm(true);
  };

  
  const handleBookingSubmit = async (bookingData: AmbulanceBookingData, pricing: PricingResult) => {
    setIsLoading(true);
    try {
      // First, find available ambulance providers
      const ambulances = await ambulanceBookingService.searchAmbulances({
        pickupLocation: bookingData.originAddress,
        destination: bookingData.destinationAddress,
        urgency: bookingData.tripType.toLowerCase() as 'priority' | 'regular',
        requiredEquipment: bookingData.ambulanceType,
        originCoordinates: bookingData.originCoordinates,
        destinationCoordinates: bookingData.destinationCoordinates
      });

      if (!ambulances || ambulances.length === 0) {
        toast.error('No ambulance providers available for this route');
        setIsLoading(false);
        return;
      }

      // Use the first available ambulance provider for now
      const selectedAmbulance = ambulances[0];

      // Create the booking using the new service
      const bookingResult = await ambulanceBookingService.createBooking({
        providerId: selectedAmbulance.provider.id,
        tripType: bookingData.tripType,
        originAddress: bookingData.originAddress,
        destinationAddress: bookingData.destinationAddress,
        originCoordinates: bookingData.originCoordinates,
        destinationCoordinates: bookingData.destinationCoordinates,
        accessNotes: bookingData.accessNotes,
        onSiteContact: bookingData.onSiteContact,
        ambulanceType: bookingData.ambulanceType,
        specialRequests: bookingData.specialRequests,
        scheduledDateTime: new Date(),
      }, selectedCase || undefined);

      if (bookingResult.success) {
        toast.success('Ambulance booking created successfully!');
        console.log('Created booking:', bookingResult);

        // Validate booking has an ID before navigating to payment page
        if (!bookingResult.booking || !bookingResult.booking.id) {
          throw new Error('Booking created but no valid booking ID returned');
        }

        // AIAHAD INTEGRATION: Navigate directly to payment page instead of showing modal
        navigate(`/bookings/${bookingResult.booking.id}/payment`);
      } else {
        throw new Error('Failed to create booking');
      }

    } catch (error: any) {
      console.error('Failed to create ambulance booking:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create ambulance booking. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookingCancel = () => {
    setShowBookingForm(false);
    setSelectedCase(null);
  };

  if (isLoading && showBookingForm) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Book Hearse
        </h1>
        <p className="text-gray-600">
          Book emergency hearse transport with detailed specifications and real-time pricing.
        </p>
      </div>

      {!showBookingForm ? (
        <div className="space-y-6">
          {/* Existing Cases Selection */}
          {existingCases.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Add to Existing Case
              </h2>
              <p className="text-gray-600 mb-4">
                Select an existing case to add this ambulance booking to:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {existingCases.map((caseItem) => (
                  <div
                    key={caseItem.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 cursor-pointer transition-colors"
                    onClick={() => handleCaseSelect(caseItem.caseNumber)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {caseItem.caseNumber}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {caseItem.deceased?.name}
                        </p>
                      </div>
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                        {caseItem.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">
                      Created: {new Date(caseItem.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New Booking Option */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Create New Booking
            </h2>
            <p className="text-gray-600 mb-4">
              Book a hearse without linking to an existing case:
            </p>
            <button
              onClick={handleNewBooking}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Create Hearse Booking
            </button>
          </div>
        </div>
      ) : (
        <div>
          {/* Back Button */}
          <button
            onClick={handleBookingCancel}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-900"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Case Selection
          </button>

          {/* Selected Case Info */}
          {selectedCase && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">
                    Adding to Case: {selectedCase}
                  </h3>
                  <p className="text-sm text-green-700">
                    This ambulance booking will be added to your selected case.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Booking Form */}
          <AmbulanceBookingForm
            onSubmit={handleBookingSubmit}
            onCancel={handleBookingCancel}
            disabled={isLoading}
          />
        </div>
      )}
    </div>
  );
};

export default AmbulanceBookingPage;